/**
 * Country and currency data for workplace settings
 * Based on ISO 3166-1 alpha-2 country codes and ISO 4217 currency codes
 */

export interface CountryOption {
  code: string; // ISO 3166-1 alpha-2
  name: string;
  currency: string; // ISO 4217
  currencySymbol: string;
}

/**
 * Comprehensive list of countries with their currency information
 * Sorted alphabetically by country name for easy selection
 */
export const COUNTRIES: CountryOption[] = [
  { code: 'AU', name: 'Australia', currency: 'AUD', currencySymbol: '$' },
  { code: 'AT', name: 'Austria', currency: 'EUR', currencySymbol: '€' },
  { code: 'BE', name: 'Belgium', currency: 'EUR', currencySymbol: '€' },
  { code: 'BR', name: 'Brazil', currency: 'BRL', currencySymbol: 'R$' },
  { code: 'CA', name: 'Canada', currency: 'CAD', currencySymbol: '$' },
  { code: 'CN', name: 'China', currency: 'CNY', currencySymbol: '¥' },
  { code: 'DK', name: 'Denmark', currency: 'DKK', currencySymbol: 'kr' },
  { code: 'FI', name: 'Finland', currency: 'EUR', currencySymbol: '€' },
  { code: 'FR', name: 'France', currency: 'EUR', currencySymbol: '€' },
  { code: 'DE', name: 'Germany', currency: 'EUR', currencySymbol: '€' },
  { code: 'HK', name: 'Hong Kong', currency: 'HKD', currencySymbol: 'HK$' },
  { code: 'IN', name: 'India', currency: 'INR', currencySymbol: '₹' },
  { code: 'ID', name: 'Indonesia', currency: 'IDR', currencySymbol: 'Rp' },
  { code: 'IE', name: 'Ireland', currency: 'EUR', currencySymbol: '€' },
  { code: 'IT', name: 'Italy', currency: 'EUR', currencySymbol: '€' },
  { code: 'JP', name: 'Japan', currency: 'JPY', currencySymbol: '¥' },
  { code: 'MY', name: 'Malaysia', currency: 'MYR', currencySymbol: 'RM' },
  { code: 'MX', name: 'Mexico', currency: 'MXN', currencySymbol: '$' },
  { code: 'NL', name: 'Netherlands', currency: 'EUR', currencySymbol: '€' },
  { code: 'NZ', name: 'New Zealand', currency: 'NZD', currencySymbol: '$' },
  { code: 'NO', name: 'Norway', currency: 'NOK', currencySymbol: 'kr' },
  { code: 'PH', name: 'Philippines', currency: 'PHP', currencySymbol: '₱' },
  { code: 'PL', name: 'Poland', currency: 'PLN', currencySymbol: 'zł' },
  { code: 'PT', name: 'Portugal', currency: 'EUR', currencySymbol: '€' },
  { code: 'SG', name: 'Singapore', currency: 'SGD', currencySymbol: '$' },
  { code: 'ZA', name: 'South Africa', currency: 'ZAR', currencySymbol: 'R' },
  { code: 'KR', name: 'South Korea', currency: 'KRW', currencySymbol: '₩' },
  { code: 'ES', name: 'Spain', currency: 'EUR', currencySymbol: '€' },
  { code: 'SE', name: 'Sweden', currency: 'SEK', currencySymbol: 'kr' },
  { code: 'CH', name: 'Switzerland', currency: 'CHF', currencySymbol: 'CHF' },
  { code: 'TH', name: 'Thailand', currency: 'THB', currencySymbol: '฿' },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', currencySymbol: '£' },
  { code: 'US', name: 'United States', currency: 'USD', currencySymbol: '$' },
  { code: 'VN', name: 'Vietnam', currency: 'VND', currencySymbol: '₫' },
];

/**
 * Get country information by country code
 */
export const getCountryByCode = (code: string): CountryOption | undefined => {
  return COUNTRIES.find(country => country.code === code);
};

/**
 * Get currency symbol by currency code
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  const country = COUNTRIES.find(c => c.currency === currencyCode);
  return country?.currencySymbol || currencyCode;
};

/**
 * Search countries by name (case-insensitive)
 */
export const searchCountries = (query: string): CountryOption[] => {
  if (!query.trim()) return COUNTRIES;
  
  const searchTerm = query.toLowerCase();
  return COUNTRIES.filter(country => 
    country.name.toLowerCase().includes(searchTerm) ||
    country.code.toLowerCase().includes(searchTerm)
  );
};

/**
 * Get all available currencies from the countries list
 */
export const getAvailableCurrencies = (): Array<{ code: string; symbol: string }> => {
  const currencies = new Map<string, string>();
  
  COUNTRIES.forEach(country => {
    currencies.set(country.currency, country.currencySymbol);
  });
  
  return Array.from(currencies.entries()).map(([code, symbol]) => ({
    code,
    symbol
  })).sort((a, b) => a.code.localeCompare(b.code));
};
